import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Terminal, Clock, User, Server, Activity, Filter, Play, FileTerminal, AlertTriangle, SearchX, Eye, Video } from 'lucide-react';
import { terminalLogsApi, filesApi } from '../services/api';
import { TerminalSessionWithLogs } from '../types';
import { useQuery } from 'react-query';
import { fetchUserFilterOptions, userFilterHelp, userFilterQuery } from '../lib/userFilterOptions';
import { User as IAMUser } from '../types/iam';
import { sessionTone } from '@/lib/accessDecision';
import { Button, Card, CardButton, Chip, EmptyState, Input, ListPageSkeleton, Modal, MonoCell, OutcomeBadge, PageHeader, PaginationBar, Select, Timestamp, TimeRangePicker, type ChipVariant } from '@/components/ds-zta';
import { useUrlState, useUrlTimeRange } from '@/lib/useUrlState';
import { DEFAULT_RANGE, resolveRange } from '@/lib/timeRange';
import { windowPhrase } from '@/features/audit/timeWindow';
import RedactedText from '@/components/terminal/RedactedText';
import { redactSecrets } from '@/lib/redact';
import { usePermissions } from '../hooks/usePermissions';

// Filters, paging and the time range live in the URL (design eval F24, F26):
// Back, refresh and a pasted link reopen the same view.
const URL_DEFAULTS = { user_id: '', resource_name: '', status: '', page: '1', limit: '20' };
type FilterKey = 'user_id' | 'resource_name' | 'status';
const EMPTY_FILTERS: Record<FilterKey, string> = { user_id: '', resource_name: '', status: '' };

const SSHActivityLogs: React.FC = () => {
  const navigate = useNavigate();
  // The join page and the player are admin-only routes (F27), so the links
  // to them show only to someone the route guard would let through.
  const { canAccessRoute } = usePermissions();
  const mayOpenSession = canAccessRoute('/sessions');
  const [sessions, setSessions] = useState<TerminalSessionWithLogs[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const [range, setRange] = useUrlTimeRange(DEFAULT_RANGE, ['page']);
  const appliedFilters = useMemo(
    () => ({ user_id: url.user_id, resource_name: url.resource_name, status: url.status }),
    [url.user_id, url.resource_name, url.status],
  );
  const [filters, setFilters] = useState(appliedFilters);
  const limit = Math.max(1, Number(url.limit) || 20);
  const page = Math.max(1, Number(url.page) || 1);
  const offset = (page - 1) * limit;
  const [total, setTotal] = useState(0);

  // Video modal state
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [loadingBlob, setLoadingBlob] = useState<string | null>(null);

  // Filter modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Fetch users for dropdown
  const { data: users, isLoading: isLoadingUsers } = useQuery(userFilterQuery, fetchUserFilterOptions);

  const openVideo = async (blobName: string) => {
    setVideoLoading(true);
    setLoadingBlob(blobName);
    setVideoError(null);
    try {
      const response = await filesApi.downloadByBlobName(blobName);
      const url = URL.createObjectURL(response.data);
      setVideoUrl(url);
      setVideoOpen(true);
    } catch (e: any) {
      setVideoError(e?.message || 'Failed to load recording');
    } finally {
      setVideoLoading(false);
      setLoadingBlob(null);
    }
  };

  const closeVideo = () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setVideoOpen(false);
  };

  // One source of truth: this effect is the only place that fetches, keyed on
  // the applied filters and the page. Filter changes, chip removal and Refresh
  // only change state (Refresh bumps reloadKey). Each run takes a request id and
  // a response is applied only if it is still the latest, so a slow earlier
  // request can never overwrite rows for the filters now shown as chips.
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);
  const reload = () => setReloadKey((k) => k + 1);

  const rangeKey = JSON.stringify(range);
  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, appliedFilters, rangeKey, reloadKey]);

  const fetchSessions = async () => {
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestId === requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // The time range is resolved here, at query time, so a relative range
      // ("last 24 hours") moves with every fetch. The server filters by it.
      const bounds = resolveRange(range);
      const params = {
        limit,
        offset,
        ...(appliedFilters.user_id && { user_id: appliedFilters.user_id }),
        ...(appliedFilters.resource_name && { resource_name: appliedFilters.resource_name }),
        ...(appliedFilters.status && { status: appliedFilters.status }),
        ...(bounds.from && { start_date: bounds.from }),
        ...(bounds.to && { end_date: bounds.to }),
      };

      const response = await terminalLogsApi.listSessionsGrouped(params);
      if (!isLatest()) return;
      setSessions(response.data.sessions || []);
      // Only the total comes from the response. Copying limit/offset back into
      // the URL would re-run the effect whenever the server echoed a different value.
      setTotal(response.data.total || 0);
    } catch (err) {
      if (!isLatest()) return;
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // An event type is a category, not a status: drawn as a ds Chip.
  const getEventTypeVariant = (eventType: string): ChipVariant => {
    switch (eventType) {
      case 'command':
      case 'exec':
        return 'info';
      case 'output':
        return 'neutral';
      case 'error':
        return 'danger';
      default:
        return 'default';
    }
  };


  // Every filter change starts the results over at page 1.
  const applyFilters = () => {
    setUrl({ ...filters, page: null });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setUrl({ ...EMPTY_FILTERS, page: null });
  };

  const removeFilter = (filterKey: FilterKey) => {
    setUrl({ [filterKey]: null, page: null });
  };

  const getActiveFilters = () => {
    const active: { key: FilterKey; label: string; value: string }[] = [];
    if (appliedFilters.user_id) {
      const usersList = Array.isArray(users) ? users : users?.data || [];
      const user = usersList.find((u: IAMUser) => u.id === appliedFilters.user_id);
      active.push({ key: 'user_id', label: 'User', value: user?.name || user?.email || appliedFilters.user_id });
    }
    if (appliedFilters.resource_name) active.push({ key: 'resource_name', label: 'Resource', value: appliedFilters.resource_name });
    if (appliedFilters.status) active.push({ key: 'status', label: 'Status', value: appliedFilters.status });
    return active;
  };

  const handlePageChange = (newPage: number) => {
    setUrl({ page: newPage });
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setUrl({ limit: newLimit, page: null });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
        <PageHeader
          className="mb-6"
          title="SSH Terminal Activity Logs"
          icon={<FileTerminal />}
          description="Monitor and review SSH terminal sessions and commands"
          secondaryActions={
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Filter className="w-4 h-4" />}
              onClick={() => { setFilters(appliedFilters); setFilterModalOpen(true); }} // the modal edits a draft of what is applied
            >
              Filters
            </Button>
          }
          timeRange={<TimeRangePicker value={range} onChange={setRange} label="Session start time range" allowAll />}
          onRefresh={reload}
          refreshing={loading}
        />

        {/* Active Filters Badges */}
        {getActiveFilters().length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-[var(--d2d-text-secondary)]">Active filters:</span>
            {getActiveFilters().map((filter) => (
              <Chip
                key={filter.key}
                size="md"
                onRemove={() => removeFilter(filter.key)}
                removeLabel={`Remove ${filter.label} filter`}
              >
                <span className="font-medium">{filter.label}:</span>{' '}
                <span>{filter.value}</span>
              </Chip>
            ))}
            {getActiveFilters().length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
              >
                Clear all
              </Button>
            )}
          </div>
        )}

      {/* Loading State */}
      {loading && (
        <Card className="p-5">
          <ListPageSkeleton aria-label="Loading terminal sessions" filterCount={0} rowCount={6} />
        </Card>
      )}

      {/* Error State */}
      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Failed to load terminal sessions"
            description={error}
            action={<Button size="sm" variant="secondary" onClick={reload}>Retry</Button>}
          />
        </Card>
      )}

      {/* Sessions List */}
      {!loading && !error && (
        <div className="space-y-4">
          {sessions.length === 0 ? (
            <Card>
              {getActiveFilters().length > 0 || range.kind !== 'all' ? (
                <EmptyState
                  icon={<SearchX />}
                  title={getActiveFilters().length > 0
                    ? `No terminal sessions match these filters ${windowPhrase(range)}`.trim()
                    : `No terminal sessions started ${windowPhrase(range)}`}
                  description="Try adjusting your filters or widening the time range."
                />
              ) : (
                <EmptyState
                  icon={<Terminal />}
                  title="No terminal sessions yet"
                  description="SSH sessions and the commands run in them will appear here."
                />
              )}
            </Card>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className="bg-[var(--d2d-bg-card)] rounded-lg border border-[var(--d2d-border)] overflow-hidden"
              >
                {/* Session Header: a real button (design eval F43), so the
                    row expands from the keyboard too. Play is its own control
                    beside it, not nested inside. */}
                <div className="flex items-stretch hover:bg-[var(--ds-color-bg-hover)] transition-colors">
                  <CardButton
                    className="flex-1 min-w-0 p-4"
                    aria-expanded={expandedSessions.has(session.id)}
                    aria-controls={`ssh-session-logs-${session.id}`}
                    onClick={() => toggleSession(session.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {expandedSessions.has(session.id) ? (
                          <ChevronDown aria-hidden className="w-5 h-5 text-[var(--d2d-text-muted)]" />
                        ) : (
                          <ChevronRight aria-hidden className="w-5 h-5 text-[var(--d2d-text-muted)]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg font-medium text-[var(--d2d-text-primary)]">
                            {session.resource_name}
                          </span>
                          <OutcomeBadge value={session.status} tone={sessionTone(session.status)} shape="chip">
                            {session.status}
                          </OutcomeBadge>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
                          <div className="flex items-center gap-2 text-[var(--d2d-text-secondary)]">
                            <User aria-hidden className="w-4 h-4" />
                            <span>{session.user_email || session.user_id}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[var(--d2d-text-secondary)]">
                            <Server aria-hidden className="w-4 h-4" />
                            <span>{session.ssh_user || '—'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[var(--d2d-text-secondary)]">
                            <Clock aria-hidden className="w-4 h-4" />
                            <Timestamp value={session.started_at} />
                          </div>
                          <div className="flex items-center gap-2 text-[var(--d2d-text-secondary)]">
                            <Activity aria-hidden className="w-4 h-4" />
                            <span>{session.command_count} commands</span>
                          </div>
                        </div>
                        {!!session.duration_seconds && (
                          <div className="mt-2 text-sm text-[var(--d2d-text-secondary)]">
                            Duration: {formatDuration(session.duration_seconds)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardButton>
                  {/* Replay is the server's record of the session, in the same
                      player as Sessions (design eval F19, D2 = A). The video
                      footage is secondary: the session's own browser recorded
                      and uploaded it, so it is labelled as such. */}
                  {((session.session_id && mayOpenSession) || session.footage_url) && (
                    <div className="flex items-end gap-2 p-4 pl-0">
                      {session.session_id && mayOpenSession && (session.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/sessions/${encodeURIComponent(session.session_id)}/join`)}
                          leadingIcon={<Eye className="w-4 h-4" />}
                          aria-label={`Watch the live session on ${session.resource_name} (read-only)`}
                        >
                          Watch
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/sessions/${encodeURIComponent(session.session_id)}/player`)}
                          leadingIcon={<Play className="w-4 h-4" />}
                          aria-label={`Replay the server recording of ${session.resource_name}`}
                        >
                          Replay
                        </Button>
                      ))}
                      {session.footage_url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openVideo(session.footage_url!)}
                          loading={loadingBlob === session.footage_url}
                          leadingIcon={<Video className="w-4 h-4" />}
                          aria-label={`Browser recording of ${session.resource_name} (uploaded by the session's own browser)`}
                        >
                          Browser recording
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Session Logs (Expanded) */}
                {expandedSessions.has(session.id) && (
                  <div id={`ssh-session-logs-${session.id}`} className="border-t border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)]">
                    <div className="p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <h4 className="text-sm font-medium text-[var(--d2d-text-secondary)]">
                          Activity Logs ({session.total_logs} total)
                        </h4>
                        <span className="flex items-center gap-1.5 text-xs text-[var(--d2d-text-secondary)]">
                          Session ID <MonoCell value={session.id} width="24ch" />
                        </span>
                      </div>
                      {session.logs.length === 0 ? (
                        <p className="text-sm text-[var(--d2d-text-muted)]">No activity logs recorded</p>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {session.logs.map((log) => (
                            <div
                              key={log.id}
                              className="bg-[var(--d2d-bg-card)] rounded-lg p-3 border-2 border-[var(--d2d-border)]"
                            >
                              <div className="flex items-start justify-between">
                                <Chip variant={getEventTypeVariant(log.event_type)}>
                                  {log.event_type}
                                </Chip>
                                <span className="text-xs text-[var(--d2d-text-muted)]">
                                  <Timestamp value={log.timestamp} />
                                </span>
                              </div>
                              
                              {log.command && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-[var(--d2d-text-secondary)]">Command:</span>
                                  <RedactedText
                                    value={log.command}
                                    label="command"
                                    className="mt-1 p-2 bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] rounded text-sm font-mono overflow-x-auto"
                                  />
                                </div>
                              )}
                              
                              {log.output && (
                                <div className="mt-2">
                                  <span className="text-xs font-medium text-[var(--d2d-text-secondary)]">Output:</span>
                                  <RedactedText
                                    value={log.output}
                                    label="output"
                                    className="mt-1 p-2 bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] rounded text-sm font-mono overflow-x-auto max-h-40 overflow-y-auto"
                                  />
                                </div>
                              )}
                              
                              {log.exit_code != null && (
                                <div className="text-xs text-[var(--d2d-text-secondary)]">
                                  Exit Code: <span className={log.exit_code === 0 ? 'text-[var(--d2d-success)]' : 'text-[var(--danger-text)]'}>{log.exit_code}</span>
                                </div>
                              )}
                              
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div className="mt-2 text-xs text-[var(--d2d-text-secondary)]">
                                  <span className="font-medium">Metadata:</span>
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {Object.entries(log.metadata).map(([key, value]) => (
                                      <span key={key} className="px-2 py-1 bg-[var(--ds-color-bg-subtle)] rounded">
                                        {redactSecrets(`${key}: ${String(value)}`)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && sessions.length > 0 && (
        <div className='mt-4'>
        {total > 0 && <PaginationBar
          page={page}
          totalRows={total}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleItemsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />}
        </div>
      )}

      {/* Video Modal */}
      <Modal
        open={videoOpen}
        onClose={closeVideo}
        size="xl"
        dismissOnOverlay={false}
        title="Browser recording"
        description="Uploaded by the session's own browser. The server's record of the session is the evidence: use Replay."
      >
        {videoLoading && (
          <div className="text-[var(--d2d-text-muted)] text-sm">Loading video...</div>
        )}
        {videoError && (
          <div className="text-[var(--danger-text)] text-sm mb-2">{videoError}</div>
        )}
        {videoUrl && (
          <video src={videoUrl} controls autoPlay className="w-full rounded bg-[var(--d2d-bg-secondary)]" />
        )}
      </Modal>

      {/* Filter Modal */}
      <Modal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        size="lg"
        dismissOnOverlay={false}
        title={
          <span className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filter Sessions
          </span>
        }
        footer={
          <>
            <Button variant="secondary" onClick={resetFilters}>
              Reset
            </Button>
            <Button
              onClick={() => {
                applyFilters();
                setFilterModalOpen(false);
              }}
            >
              Apply Filters
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <p className="md:col-span-2 text-sm text-[var(--d2d-text-secondary)]">
            The time window is set with the time range in the page header.
          </p>
          <Select
            label="User ID"
            help={userFilterHelp(Array.isArray(users) ? undefined : users)}
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          >
            <option value="">All Users</option>
            {users && (Array.isArray(users) ? users : users?.data || []).map((user: IAMUser) => (
              <option key={user.id} value={user.id}>{user.name || user.id}</option>
            ))}
          </Select>
          <Input
            label="Resource Name"
            type="text"
            value={filters.resource_name}
            onChange={(e) => setFilters({ ...filters, resource_name: e.target.value })}
            placeholder="Filter by resource..."
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="terminated">Terminated</option>
            <option value="error">Error</option>
          </Select>
        </div>
      </Modal>
    </div>

  );
};

export default SSHActivityLogs;